import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading, Card } from "@/components/Shared";
import { RecordHeader, RecordLayout, DetailPanel, Field, RelatedList, col, paymentsFromInvoices, PAYMENT_COLS } from "@/components/Record";
import { openPdf } from "@/components/DocParts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import { formatINR, formatDate } from "@/lib/format";
import { FileDown, Send, Loader2, MessageSquare, FileText, Store, Copy, Trash2, UserPlus, Pencil, X } from "lucide-react";
import { RecordSideTabs } from "@/components/Activities";
import { ContactActions } from "@/components/ContactActions";
import { INDUSTRIES, EMPLOYEE_SIZES, CONTACT_TITLES } from "@/lib/customerMeta";

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { tenant } = useAuth();
  const showVisits = tenant?.modules?.field !== false;
  const [c, setC] = useState(null);
  const [stmt, setStmt] = useState(null);
  const [sending, setSending] = useState(false);
  const reload = () => api.get(`/customers/${id}`).then(({ data }) => setC(data));
  useEffect(() => {
    reload();
    api.get(`/customers/${id}/statement`).then(({ data }) => setStmt(data));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  const E = (field, type, extra = {}) => ({ collection: "customers", id, field, type, onSaved: reload, ...extra });
  const sendStatement = async () => {
    setSending(true);
    try { await api.post(`/customers/${id}/statement/send`); toast.success("Statement queued on WhatsApp"); }
    catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };
  const del = async () => {
    if (!window.confirm(`Delete customer "${c.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/customers/${id}`); toast.success("Customer deleted"); navigate("/customers"); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!c || !c.id) return <Loading />;
  const payments = paymentsFromInvoices(c.invoices);

  return (
    <div data-testid="customer-detail">
      <RecordHeader kind="customer" title={c.name} subtitle={`${c.company || "Individual"} · +91 ${c.phone}`}
        actions={<>
          <ContactActions phone={c.phone} message={`Hi ${c.name || ""},`} className="mr-1" />
          {c.conversation && <Button variant="outline" className="h-10" onClick={() => navigate(`/inbox?c=${c.conversation.id}`)} data-testid="customer-inbox-btn"><MessageSquare className="w-4 h-4" /> WhatsApp</Button>}
          <Button variant="outline" className="h-10" onClick={() => openPdf(`/customers/${id}/statement.pdf`, `Statement-${c.number}.pdf`)} data-testid="statement-pdf-btn"><FileDown className="w-4 h-4" /> Statement</Button>
          <Button variant="outline" className="h-10" onClick={sendStatement} disabled={sending || !stmt?.has_conversation} data-testid="statement-send-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send statement</Button>
          <Button className="h-10 bg-[#0B7C5A] hover:bg-[#09664a]" onClick={() => navigate(`/quotes/new?customer_id=${id}`)} data-testid="customer-new-quote-btn"><FileText className="w-4 h-4" /> New quote</Button>
          <Button variant="outline" className="h-10" onClick={() => navigate(`/customers?clone=${id}`)} data-testid="customer-clone-btn"><Copy className="w-4 h-4" /> Clone</Button>
          <Button variant="outline" className="h-10 border-[#F83A84] text-[#F83A84]" onClick={del} data-testid="customer-delete-btn"><Trash2 className="w-4 h-4" /> Delete</Button>
        </>} />

      <RecordLayout
        left={<>
          {stmt && (
            <Card className="p-4 grid grid-cols-3 gap-2" data-testid="statement-summary">
              <div><div className="text-[11px] text-[#6B7189]">Outstanding</div><div className={`text-lg font-bold ${stmt.balance_due > 0 ? "text-[#F83A84]" : "text-[#0B7C5A]"}`} data-testid="statement-balance">{formatINR(stmt.balance_due)}</div></div>
              <div><div className="text-[11px] text-[#6B7189]">Overdue</div><div className="text-lg font-bold text-[#101D51]">{formatINR(stmt.overdue)}</div></div>
              <div><div className="text-[11px] text-[#6B7189]">Open inv.</div><div className="text-lg font-bold text-[#101D51]">{stmt.open_invoices}</div></div>
            </Card>)}          <DetailPanel>
            <Field label="Customer number" value={c.number} mono />
            <Field label="Name" value={c.name} edit={E("name")} />
            <Field label="Company" value={c.company} edit={E("company")} />
            <Field label="Phone" value={`+91 ${c.phone}`} />
            <Field label="Email" value={c.email} edit={E("email", "email")} />
            <Field label="GSTIN" value={c.gstin} mono edit={E("gstin")} />
            <Field label="Billing address" value={c.billing_address} edit={E("billing_address", "textarea")} />
            <Field label="Shipping address" value={c.shipping_address} edit={E("shipping_address", "textarea")} />
            <Field label="City" value={c.city} edit={E("city")} />
            <Field label="State" value={c.state} edit={E("state")} />
            <Field label="Industry" value={c.industry} edit={E("industry", "select", { options: ["", ...INDUSTRIES] })} />
            <Field label="Employee size" value={c.employee_size} edit={E("employee_size", "select", { options: ["", ...EMPLOYEE_SIZES] })} />
            <Field label="Created" value={formatDate(c.created_at)} />
            <Field label="Notes" value={c.notes} edit={E("notes", "textarea")} />
          </DetailPanel>
          <ContactsCard c={c} onSaved={reload} />
          <PortalAccessCard c={c} onSaved={reload} />
        </>}
        right={<RecordSideTabs object="customer" objectId={c.id} subject={c.company || c.name} customerId={c.id} testid="cust-tabs" showVisits={showVisits}
          relatedCount={c.enquiries.length + c.quotes.length + c.orders.length + c.invoices.length}
          related={<>
          <RelatedList kind="enquiry" rows={c.enquiries.map((e) => ({ ...e, path: `/enquiries/${e.id}` }))}
            columns={[col.number(), col.status(), { key: "priority", label: "Priority" }, col.date("received_at", "Received"), col.money("quote_value", "Quote value")]} />
          <RelatedList kind="quote" rows={c.quotes.map((q) => ({ ...q, path: `/quotes/${q.id}` }))} onNew={() => navigate(`/quotes/new?customer_id=${id}`)}
            columns={[col.number(), col.status(), { key: "version", label: "Ver", render: (q) => `v${q.version}` }, col.date(), col.date("valid_until", "Valid until"), col.money()]} />
          <RelatedList kind="order" title="Sales Orders" rows={c.orders.map((o) => ({ ...o, path: `/orders/${o.id}` }))} onNew={() => navigate(`/orders/new?customer_id=${id}`)}
            columns={[col.number(), col.status(), col.text("customer_po_number", "Customer PO"), col.date(), col.money(), col.money("invoiced_total", "Invoiced")]} />
          <RelatedList kind="invoice" rows={c.invoices.map((i) => ({ ...i, path: `/invoices/${i.id}` }))} onNew={() => navigate(`/invoices/new?customer_id=${id}`)}
            columns={[col.number(), col.status(), col.date(), col.date("due_date", "Due"), col.money(), col.money("balance_due", "Balance")]} />
          <RelatedList kind="payment" rows={payments} columns={PAYMENT_COLS} />
          </>} />} />
    </div>
  );
}

function PortalAccessCard({ c, onSaved }) {
  const [books, setBooks] = useState([]);
  const [pb, setPb] = useState(c.price_book_id || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/price-books").then(({ data }) => setBooks(data)); }, []);
  useEffect(() => { setPb(c.price_book_id || ""); }, [c.price_book_id]);
  const save = async (enabled) => {
    setSaving(true);
    try { await api.post(`/portal/access/${c.id}`, { enabled, price_book_id: pb || null }); toast.success(enabled ? "Retailer portal enabled" : "Portal access disabled"); onSaved(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Card className="p-4" data-testid="portal-access-card">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-[#6B7189] flex items-center gap-1"><Store className="w-3.5 h-3.5" /> Retailer portal</div>
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.portal_enabled ? "bg-[#0B7C5A]/10 text-[#0B7C5A]" : "bg-[#E6E8F0] text-[#6B7189]"}`} data-testid="portal-status">{c.portal_enabled ? "Enabled" : "Off"}</span>
      </div>
      <p className="text-xs text-[#6B7189] mb-3">Retailer signs in at <span className="font-mono text-[#101D51]">/portal</span> with +91 {c.phone}, browses your catalogue at their price-book prices and submits POs.</p>
      <label className="text-[11px] text-[#6B7189]">Price book for this retailer</label>
      <select value={pb} onChange={(e) => setPb(e.target.value)} className="mt-1 h-9 w-full rounded-md border border-[#E6E8F0] px-2 text-sm text-[#101D51]" data-testid="portal-pricebook-select">
        <option value="">Standard (list price)</option>
        {books.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
      </select>
      <div className="flex gap-2 mt-3">
        <Button className="h-9 bg-[#0B7C5A] hover:bg-[#09664a] flex-1" onClick={() => save(true)} disabled={saving} data-testid="portal-enable-btn">{c.portal_enabled ? "Save" : "Enable portal"}</Button>
        {c.portal_enabled && <Button variant="outline" className="h-9 text-[#F83A84]" onClick={() => save(false)} disabled={saving} data-testid="portal-disable-btn">Disable</Button>}
      </div>
    </Card>);
}

function ContactsCard({ c, onSaved }) {
  const empty = { title: "", first_name: "", last_name: "", email: "", phone: "", comment: "" };
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);
  const contacts = c.contacts || [];
  const openNew = () => { setEditing(null); setF(empty); setOpen(true); };
  const openEdit = (ct) => { setEditing(ct); setF({ ...empty, ...ct }); setOpen(true); };
  const save = async () => {
    if (!f.first_name.trim()) return toast.error("First name is required");
    setSaving(true);
    try {
      if (editing) await api.put(`/customers/${c.id}/contacts/${editing.id}`, f);
      else await api.post(`/customers/${c.id}/contacts`, f);
      toast.success("Contact saved"); setOpen(false); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const del = async (ct) => {
    if (!window.confirm(`Remove contact ${ct.first_name}?`)) return;
    try { await api.delete(`/customers/${c.id}/contacts/${ct.id}`); toast.success("Contact removed"); onSaved(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Card className="p-4" data-testid="contacts-card">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-[#6B7189] flex items-center gap-1"><UserPlus className="w-3.5 h-3.5" /> Contacts</div>
        <Button variant="outline" className="h-8 px-2 text-xs" onClick={openNew} data-testid="contact-add-btn"><UserPlus className="w-3.5 h-3.5" /> Add contact</Button>
      </div>
      {contacts.length === 0 ? <p className="text-xs text-[#6B7189]">No contacts yet. Add the people you deal with at this customer.</p> : (
        <div className="divide-y divide-[#F0F1F5]">
          {contacts.map((ct) => (
            <div key={ct.id} className="py-2 flex items-start gap-2" data-testid={`contact-row-${ct.id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-[#101D51] truncate">{[ct.title, ct.first_name, ct.last_name].filter(Boolean).join(" ")}</div>
                <div className="text-[11px] text-[#6B7189] truncate">{[ct.phone && `+91 ${ct.phone}`, ct.email].filter(Boolean).join(" · ") || "—"}</div>
                {ct.comment && <div className="text-[11px] text-[#9AA0B5] truncate">{ct.comment}</div>}
              </div>
              {ct.phone && <ContactActions phone={ct.phone} size="sm" suffix={`-${ct.id}`} message={`Hi ${ct.first_name || ""},`} />}
              <button onClick={() => openEdit(ct)} className="text-[#6B7189] hover:text-[#101D51] p-1" data-testid={`contact-edit-${ct.id}`}><Pencil className="w-3.5 h-3.5" /></button>
              <button onClick={() => del(ct)} className="text-[#6B7189] hover:text-[#F83A84] p-1" data-testid={`contact-del-${ct.id}`}><X className="w-4 h-4" /></button>
            </div>))}
        </div>)}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="contact-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit contact" : "New contact"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Title</Label>
              <select value={f.title} onChange={set("title")} data-testid="contact-title" className="mt-1 h-10 w-full rounded-md border border-[#E6E8F0] px-2 text-sm bg-white text-[#101D51]">
                <option value="">—</option>{CONTACT_TITLES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select></div>
            <div><Label className="text-xs">First name *</Label><Input value={f.first_name} onChange={set("first_name")} className="mt-1" data-testid="contact-first-name" /></div>
            <div><Label className="text-xs">Last name</Label><Input value={f.last_name} onChange={set("last_name")} className="mt-1" data-testid="contact-last-name" /></div>
            <div><Label className="text-xs">Phone</Label><Input value={f.phone} onChange={set("phone")} className="mt-1" data-testid="contact-phone" /></div>
            <div className="col-span-2"><Label className="text-xs">Email</Label><Input value={f.email} onChange={set("email")} className="mt-1" data-testid="contact-email" /></div>
            <div className="col-span-2"><Label className="text-xs">Comment</Label><Input value={f.comment} onChange={set("comment")} className="mt-1" data-testid="contact-comment" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#101D51] hover:bg-[#080852]" data-testid="contact-save-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save contact"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>);
}
